import { createAction, handleActions } from 'redux-actions'
import { show as showModal } from 'redux-modal'
import moment from 'moment-timezone'

import { isValidEmail, getFirstAndLastName } from 'common/util/helper'
import { accountLoadHandler } from 'broker/redux/account'
import { selectIsRCCOnly } from 'broker/selectors/account'
import { selectBrokerOpId } from 'broker/selectors/auth'
import {
  selectAgentInvites,
  selectAgentInvitesToPost,
  selectNonEmptyAdminRows,
  selectSearchResults,
  selectSelectedSearchResults,
  selectSelectedInvites
} from 'broker/selectors/manage_users'
import client from 'common/util/http'
import { notify } from 'common/util/notify'

const INVALID_EMAIL_ERROR = 'Please enter a valid email address'
const MISSING_EMAIL_ERROR = 'Email address is required'
const INVALID_NAME_ERROR = 'First and last name are required'
const DUPLICATE_EMAIL_ERROR = 'Duplicate email address'

const STORE_NAME = 'manage_users'

const LOAD_AGENT_INVITES = `${STORE_NAME}/LOAD_AGENT_INVITES`
const EDIT_AGENT_INVITE = `${STORE_NAME}/EDIT_AGENT_INVITE`
const ADD_AGENT_INVITE = `${STORE_NAME}/ADD_AGENT_INVITE`
const REMOVE_SELECTED_AGENT_INVITES = `${STORE_NAME}/REMOVE_SELECTED_AGENT_INVITES`
const SEND_AGENT_INVITES_START = `${STORE_NAME}/SEND_AGENT_INVITES_START`
const SEND_AGENT_INVITES_SUCCESS = `${STORE_NAME}/SEND_AGENT_INVITES_SUCCESS`
const SEND_AGENT_INVITES_ERROR = `${STORE_NAME}/SEND_AGENT_INVITES_ERROR`
const VALIDATE_AGENT_EMAIL = `${STORE_NAME}/VALIDATE_AGENT_EMAIL`
const BULK_VALIDATE_AGENT_EMAIL = `${STORE_NAME}/BULK_VALIDATE_AGENT_EMAIL`
const PROCESS_AGENT_VALIDATION_ERRORS = `${STORE_NAME}/PROCESS_AGENT_VALIDATION_ERRORS`
const CLEAR_EMPTY_AGENT_INVITES = `${STORE_NAME}/CLEAR_EMPTY_AGENT_INVITES`
const CLEAR_SELECTED_SEARCH_RESULTS = `${STORE_NAME}/CLEAR_SELECTED_SEARCH_RESULTS`
const TOGGLE_IS_AGENT_INVITE_SELECTED = `${STORE_NAME}/TOGGLE_IS_AGENT_INVITE_SELECTED`
const UNSELECT_ALL_AGENT_INVITES = `${STORE_NAME}/UNSELECT_ALL_AGENT_INVITES`
const LOAD_SELECTED_SEARCH_RESULTS = `${STORE_NAME}/LOAD_SELECTED_SEARCH_RESULTS`
const EDIT_SELECTED_AGENT_INVITES = `${STORE_NAME}/EDIT_SELECTED_AGENT_INVITES`

const ADD_ADMIN_INVITE = `${STORE_NAME}/ADD_ADMIN_INVITE`
const EDIT_ADMIN_INVITE = `${STORE_NAME}/EDIT_ADMIN_INVITE`
const CLEAR_ADMIN_INVITES = `${STORE_NAME}/CLEAR_ADMIN_INVITES`
const SEND_ADMIN_INVITES_START = `${STORE_NAME}/SEND_ADMIN_INVITES_START`
const SEND_ADMIN_INVITES_SUCCESS = `${STORE_NAME}/SEND_ADMIN_INVITES_SUCCESS`
const SEND_ADMIN_INVITES_ERROR = `${STORE_NAME}/SEND_ADMIN_INVITES_ERROR`
const VALIDATE_ADMIN_EMAIL = `${STORE_NAME}/VALIDATE_ADMIN_EMAIL`
const VALIDATE_ADMIN_NAME = `${STORE_NAME}/VALIDATE_ADMIN_NAME`
const PROCESS_ADMIN_VALIDATION_ERRORS = `${STORE_NAME}/PROCESS_ADMIN_VALIDATION_ERRORS`

const loadAgentInvites = createAction(LOAD_AGENT_INVITES)
export const addAgentInvite = createAction(ADD_AGENT_INVITE)
export const editAgentInvite = createAction(EDIT_AGENT_INVITE)
export const validateAgentEmail = createAction(VALIDATE_AGENT_EMAIL)
export const removeSelectedAgentInvites = createAction(REMOVE_SELECTED_AGENT_INVITES)
const sendAgentInvitesStart = createAction(SEND_AGENT_INVITES_START)
const sendAgentInvitesSuccess = createAction(SEND_AGENT_INVITES_SUCCESS)
const sendAgentInvitesError = createAction(SEND_AGENT_INVITES_ERROR)
const handleAgentValidationErrors = createAction(PROCESS_AGENT_VALIDATION_ERRORS)
export const bulkValidateAgentEmail = createAction(BULK_VALIDATE_AGENT_EMAIL)
const clearEmptyAgentInvites = createAction(CLEAR_EMPTY_AGENT_INVITES)
const clearSelectedSearchResults  = createAction(CLEAR_SELECTED_SEARCH_RESULTS)
export const toggleIsAgentInviteSelected = createAction(TOGGLE_IS_AGENT_INVITE_SELECTED)
export const unselectAllAgentInvites = createAction(UNSELECT_ALL_AGENT_INVITES)
const loadSelectedSearchResults = createAction(LOAD_SELECTED_SEARCH_RESULTS)
export const editSelectedAgentInvites = createAction(EDIT_SELECTED_AGENT_INVITES)

export const addAdminInvite = createAction(ADD_ADMIN_INVITE)
export const editAdminInvite = createAction(EDIT_ADMIN_INVITE)
export const validateAdminEmail = createAction(VALIDATE_ADMIN_EMAIL)
export const validateAdminName = createAction(VALIDATE_ADMIN_NAME)
export const clearAdminInvites = createAction(CLEAR_ADMIN_INVITES)
const sendAdminInvitesStart = createAction(SEND_ADMIN_INVITES_START)
const sendAdminInvitesSuccess = createAction(SEND_ADMIN_INVITES_SUCCESS)
const sendAdminInvitesError = createAction(SEND_ADMIN_INVITES_ERROR)
const handleAdminValidationErrors = createAction(PROCESS_ADMIN_VALIDATION_ERRORS)

const cleanState = (payload = {}) => ({
  agentInvites: payload.agentInvites || [],
  adminInvites: payload.adminInvites || [toAdminInvite()],
  selectedSearchResults: [],
  removedSearchResults: [],
  sendingAgentInvites: false,
  sendingAdminInvites: false,
})

const toAgentInvite = (payload = {}) => ({
  email: payload.email || null,
  emailErrorMessage: payload.emailErrorMessage || null,
  experienceLevelErrorMessage: payload.experienceLevelErrorMessage || null,
  name: payload.name || null,
  selected: payload.selected || false,
  experience_level: payload.experience_level || null,
  party_id: payload.party_id || null,
  recommended: payload.recommended || null,
  suggested: payload.suggested || false,
  is_mvip_enabled: payload.is_mvip_enabled || false,
  is_core_enabled: payload.is_core_enabled || false,
  broker_recommendation_id: payload.broker_recommendation_id || null,
  key: payload.key || moment().format('x')              // Give it a key so we can keep "empty rows" in the invite list
})

const toAdminInvite = (payload = {}) => ({
  email: payload.email || null,
  emailErrorMessage: payload.emailErrorMessage || null,
  nameErrorMessage: payload.nameErrorMessage || null,
  roleErrorMessage: payload.roleErrorMessage || null,
  name: payload.name || null,
  phone: payload.phone || null,
  role: payload.role || null,
  key: payload.key || moment().format('x')              // Give it a key so we can keep "empty rows" in the invite list
})

export function removeSelectedAgent() {
  return (dispatch, getState) => {
    const state = getState()
    const invite = _.first(selectSelectedInvites(state))
    if (!invite.party_id) {
      dispatch(removeSelectedAgentInvites())
    } else {
      dispatch(showModal('RemoveSuggestionFeedbackModal', {
        agent_party_id: invite.party_id,
        broker_op_id: selectBrokerOpId(state)
      }))
    }
  }
}

export function handleMatchFeedbackSubmit({agent_party_id, broker_op_id, feedback_type}) {
  return async (dispatch) => {
    try {
      await client.post('remove_suggested_agent', {
        data: {agent_party_id, broker_op_id, feedback_type}
      })
    } finally {
      dispatch(removeSelectedAgentInvites())
    }
  }
}

export function postAgentInvites() {
  return async (dispatch, getState) => {
    dispatch(sendAgentInvitesStart())
    try {
      const invites = selectAgentInvitesToPost(getState())
      const is_rcc_only = selectIsRCCOnly(getState())

      invites.forEach(invite => {
        if (is_rcc_only && invite.experience_level === null) {
          invite.experience_level = 'emerging'
        }
      })

      const response = await client.post('manage_users_invite_agents', {
        data: { invites }
      })
      const results = response.results
      if (results.failed_emails || results.failed_experience_levels) {
        dispatch(handleAgentValidationErrors({ failed_emails: results.failed_emails, failed_experience_levels: results.failed_experience_levels }))
        dispatch(sendAgentInvitesSuccess())
      } else {
        dispatch(accountLoadHandler(results))
        dispatch(clearSelectedSearchResults())
        dispatch(sendAgentInvitesSuccess())
        if (invites.length === 1) {
          notify.success(`Success! An invite has been sent to ${invites[0].email}.`)
        } else {
          notify.success(`Success! Invites have been sent to ${invites.length} agents.`)
        }
        dispatch(removeSelectedAgentInvites())
        dispatch(clearEmptyAgentInvites())
      }
    } catch(err) {
      dispatch(sendAgentInvitesError(err))
    }
  }
}

export function postAdminInvites() {
  return async (dispatch, getState) => {
    dispatch(sendAdminInvitesStart())
    try {
      const contacts = selectNonEmptyAdminRows(getState())

      const response = await client.post('invite_admins', {
        data: { contacts }
      })
      const results = response.results
      if (results.failed_emails || results.failed_names || results.failed_roles) {
        dispatch(handleAdminValidationErrors({ failed_emails: results.failed_emails, failed_names: results.failed_names, failed_roles: results.failed_roles }))
        dispatch(sendAdminInvitesSuccess())
      } else {
        dispatch(accountLoadHandler(results))
        dispatch(sendAdminInvitesSuccess())
        if (contacts.length === 1) {
          notify.success(`Success! An invite has been sent to ${contacts[0].email}.`)
        } else {
          notify.success(`Success! Invites have been sent to ${contacts.length} users.`)
        }
        dispatch(clearAdminInvites())
      }
    } catch(err) {
      dispatch(sendAdminInvitesError(err))
    }
  }
}

const updateAgentInvite = (state, { payload }) => {
  const { email, name, selected, key, experience_level, is_mvip_enabled, is_core_enabled } = payload
  if (!key) {
    throw new Error('A key is required to edit an invite')
  }
  const idx = _.findIndex(state.agentInvites, i => i.key === key)
  const updatedInvite = {...state.agentInvites[idx]}
  updatedInvite.email = _.isNil(email) ? updatedInvite.email : email
  updatedInvite.name = _.isNil(name) ? updatedInvite.name : name
  updatedInvite.selected = _.isNil(selected) ? updatedInvite.selected : selected
  updatedInvite.experience_level = _.isNil(experience_level) ? updatedInvite.experience_level : experience_level
  updatedInvite.party_id = _.isNil(email) ? updatedInvite.party_id : null
  updatedInvite.recommended = _.isNil(email) ? updatedInvite.recommended : null
  updatedInvite.suggested = _.isNil(email) ? updatedInvite.suggested : false
  updatedInvite.broker_recommendation_id = _.isNil(email) ? updatedInvite.broker_recommendation_id : null
  updatedInvite.experienceLevelErrorMessage = _.isNil(experience_level) ? updatedInvite.experienceLevelErrorMessage : null
  updatedInvite.is_mvip_enabled = _.isNil(is_mvip_enabled) ? updatedInvite.is_mvip_enabled : is_mvip_enabled
  updatedInvite.is_core_enabled = _.isNil(is_core_enabled) ? updatedInvite.is_core_enabled : is_core_enabled

  return {
    ...state,
    agentInvites: [
      ...state.agentInvites.slice(0, idx),
      {...updatedInvite},
      ...state.agentInvites.slice(idx+1)
    ]
  }
}

const updateAdminInvite = (state, { payload }) => {
  const { email, name, phone, key, role } = payload
  if (!key) {
    throw new Error('A key is required to edit an invite')
  }
  const idx = _.findIndex(state.adminInvites, i => i.key === key)
  const updatedInvite = {...state.adminInvites[idx]}
  updatedInvite.email = _.isNil(email) ? updatedInvite.email : email
  updatedInvite.name = _.isNil(name) ? updatedInvite.name : name
  updatedInvite.phone = _.isNil(phone) ? updatedInvite.phone : phone
  updatedInvite.role = _.isNil(role) ? updatedInvite.role : role
  updatedInvite.roleErrorMessage = _.isNil(role) ? updatedInvite.roleErrorMessage : null
  return {
    ...state,
    adminInvites: [
      ...state.adminInvites.slice(0, idx),
      {...updatedInvite},
      ...state.adminInvites.slice(idx+1)
    ]
  }
}

const handleRemoveSelected = (state) => {
  const { agentInvites } = state
  const selectedInvites = _.filter(agentInvites, i => i.selected)
  const selectedKeys = _.map(selectedInvites, i => i.key)
  const filteredInvites = _.filter(agentInvites, i => !_.includes(selectedKeys, i.key))

  const removedSearchResults = _.filter(selectedInvites, selectedInvite => selectedInvite.suggested)

  return {
    ...state,
    agentInvites: filteredInvites,
    removedSearchResults: [...state.removedSearchResults, ..._.map(removedSearchResults, removedSearchResult => removedSearchResult.email)]
  }
}

const toggleAgentInviteSelected = (invite) => {
  if (invite.added) {
    return {...invite, selected: !invite.selected }
  } else {
    return toAgentInvite({...invite, suggested: true, selected: true})
  }
}

const handleToggleIsAgentInviteSelected = (state, { payload }) => {
  const { key, added } = payload

  const updatedInvite = toggleAgentInviteSelected(payload)
  const newSelectedSearchResults = [...state.selectedSearchResults]
  let newAgentInvites

  if (added) {
    const idx = _.findIndex(state.agentInvites, i => i.key === key)
    newAgentInvites = [
      ...state.agentInvites.slice(0, idx),
      {...updatedInvite},
      ...state.agentInvites.slice(idx+1)
    ]
  } else {
    newAgentInvites = [...state.agentInvites, updatedInvite]
    newSelectedSearchResults.push(payload.email)
  }

  return {
    ...state,
    agentInvites: newAgentInvites,
    selectedSearchResults: newSelectedSearchResults,
  }
}

export function selectAllAgentInvites() {
  return async (dispatch, getState) => {
    const agentInvites = selectAgentInvites(getState())
    const searchResults = selectSearchResults(getState())
    const selectedSearchResults = selectSelectedSearchResults(getState())

    const existingAgentInvitesSelected = _.map(agentInvites, invite => !invite.selected ? toggleAgentInviteSelected(invite) : invite)
    const searchResultsSelected = _.map(searchResults, searchResult => toggleAgentInviteSelected(searchResult))

    const newSelectedSearchResults = _.concat(selectedSearchResults, _.map(searchResultsSelected, searchResult => searchResult.email))
    const newAgentInvites = _.concat(existingAgentInvitesSelected, searchResultsSelected)

    dispatch(loadAgentInvites({ agentInvites: newAgentInvites }))
    dispatch(loadSelectedSearchResults({ selectedSearchResults: newSelectedSearchResults}))
  }
}

const reduceValidateAgentEmail = (state, { payload }) => {
  const { key } = payload
  if (!key) {
    throw new Error('A key is required to validate')
  }
  const idx = _.findIndex(state.agentInvites, i => i.key === key)
  const updatedInvite = {...state.agentInvites[idx]}
  const { email } = updatedInvite
  if (isValidEmail(email)) {
    updatedInvite.emailErrorMessage = null

    return {
      ...state,
      agentInvites: [
        ...state.agentInvites.slice(0, idx),
        {...updatedInvite},
        ...state.agentInvites.slice(idx+1)
      ]
    }
  }

  if (!email) {
    updatedInvite.emailErrorMessage = MISSING_EMAIL_ERROR
  } else {
    updatedInvite.emailErrorMessage = INVALID_EMAIL_ERROR
  }

  return {
    ...state,
    agentInvites: [
      ...state.agentInvites.slice(0, idx),
      {...updatedInvite},
      ...state.agentInvites.slice(idx+1)
    ]
  }
}

const reduceValidateAdminEmail = (state, { payload }) => {
  const { key } = payload
  if (!key) {
    throw new Error('A key is required to validate')
  }
  const idx = _.findIndex(state.adminInvites, i => i.key === key)
  const updatedInvite = {...state.adminInvites[idx]}
  const { email } = updatedInvite
  if (isValidEmail(email)) {
    updatedInvite.emailErrorMessage = null

    return {
      ...state,
      adminInvites: [
        ...state.adminInvites.slice(0, idx),
        {...updatedInvite},
        ...state.adminInvites.slice(idx+1)
      ]
    }
  }

  if (!email) {
    updatedInvite.emailErrorMessage = MISSING_EMAIL_ERROR
  } else {
    updatedInvite.emailErrorMessage = INVALID_EMAIL_ERROR
  }

  return {
    ...state,
    adminInvites: [
      ...state.adminInvites.slice(0, idx),
      {...updatedInvite},
      ...state.adminInvites.slice(idx+1)
    ]
  }
}

const reduceValidateAdminName = (state, { payload }) => {
  const { key } = payload
  if (!key) {
    throw new Error('A key is required to validate')
  }
  const idx = _.findIndex(state.adminInvites, i => i.key === key)
  const updatedInvite = {...state.adminInvites[idx]}
  const { name } = updatedInvite
  const fullName = getFirstAndLastName(name)
  if (fullName.first_name && fullName.last_name) {
    updatedInvite.nameErrorMessage = null

    return {
      ...state,
      adminInvites: [
        ...state.adminInvites.slice(0, idx),
        {...updatedInvite},
        ...state.adminInvites.slice(idx+1)
      ]
    }
  }

  updatedInvite.nameErrorMessage = INVALID_NAME_ERROR

  return {
    ...state,
    adminInvites: [
      ...state.adminInvites.slice(0, idx),
      {...updatedInvite},
      ...state.adminInvites.slice(idx+1)
    ]
  }
}

const addValidationErrors = (state, { payload }) => {
  const { failed_emails, failed_experience_levels } = payload
  const { agentInvites } = state
  const now = moment().format('x')
  const erroredInvites = _.map(agentInvites, (invite, idx) => {
    const emailFailure = _.find(failed_emails, e => _.toLower(e.email) === _.toLower(invite.email))
    const experienceLevelFailure = _.find(failed_experience_levels, e => e.key === invite.key)
    return {
      ...invite,
      emailErrorMessage: emailFailure ? emailFailure.reason : null,
      experienceLevelErrorMessage: experienceLevelFailure ? experienceLevelFailure.reason : null,
      key: emailFailure || experienceLevelFailure ? `${now}-${idx}` : invite.key,
    }
  })

  return {
    ...state,
    agentInvites: erroredInvites
  }
}

const handleBulkValidateAgentEmail = (state) => {
  const { agentInvites } = state
  const now = moment().format('x')
  const emails = new Set()
  const erroredInvites = _.map(agentInvites, (invite, idx) => {
    const newInvite = {...invite}
    const { email, selected } = newInvite
    if (!email) {
      if (selected) {
        newInvite.emailErrorMessage = MISSING_EMAIL_ERROR
        newInvite.key = `${now}-${idx}`
      }
    } else if (!isValidEmail(email)) {
      newInvite.emailErrorMessage = INVALID_EMAIL_ERROR
      newInvite.key = `${now}-${idx}`
    } else if (emails.has(email)) {
      newInvite.emailErrorMessage = DUPLICATE_EMAIL_ERROR
    } else {
      newInvite.emailErrorMessage = null
      emails.add(email)
    }

    return newInvite
  })

  return {
    ...state,
    agentInvites: erroredInvites
  }
}

const addAdminValidationErrors = (state, { payload }) => {
  const { failed_emails, failed_names, failed_roles } = payload
  const { adminInvites } = state
  const erroredInvites = _.map(adminInvites, invite => {
    const emailFailure = _.find(failed_emails, e => e.key === invite.key)
    const nameFailure = _.find(failed_names, e => e.key === invite.key)
    const roleFailure = _.find(failed_roles, e => e.key === invite.key)
    return {
      ...invite,
      emailErrorMessage: emailFailure ? emailFailure.reason : null,
      nameErrorMessage: nameFailure ? nameFailure.reason : null,
      roleErrorMessage: roleFailure ? roleFailure.reason : null,
    }
  })

  return {
    ...state,
    adminInvites: erroredInvites
  }
}

export default handleActions({
  [LOAD_AGENT_INVITES]: (state, { payload }) => ({
    ...state,
    agentInvites: payload.agentInvites
  }),
  [ADD_AGENT_INVITE]: (state, { payload }) => ({
    ...state,
    agentInvites: (() => {
      if (!payload) {
        payload = {}
      }
      const newAgentInvites = [...state.agentInvites]
      newAgentInvites.push(toAgentInvite(payload))
      return newAgentInvites
    })(),
    selectedSearchResults: !payload.added && payload.email
      ? [...state.selectedSearchResults, payload.email]
      : state.selectedSearchResults
  }),

  [SEND_AGENT_INVITES_START]: (state) => ({
    ...state,
    sendingAgentInvites: true
  }),

  [SEND_AGENT_INVITES_SUCCESS]: (state) => ({
    ...state,
    sendingAgentInvites: false
  }),

  [SEND_AGENT_INVITES_ERROR]: (state) => ({
    ...state,
    sendingAgentInvites: false
  }),

  [EDIT_AGENT_INVITE]: updateAgentInvite,
  [VALIDATE_AGENT_EMAIL]: reduceValidateAgentEmail,
  [PROCESS_AGENT_VALIDATION_ERRORS]: addValidationErrors,
  [REMOVE_SELECTED_AGENT_INVITES]: handleRemoveSelected,


  [ADD_ADMIN_INVITE]: (state, { payload }) => ({
    ...state,
    adminInvites: (() => {
      if (!payload) {
        payload = {}
      }
      const newAdminInvites = [...state.adminInvites]
      newAdminInvites.push(toAdminInvite(payload))
      return newAdminInvites
    })()
  }),

  [EDIT_ADMIN_INVITE]: updateAdminInvite,
  [CLEAR_ADMIN_INVITES]: (state) => ({
    ...state,
    adminInvites: []
  }),

  [SEND_ADMIN_INVITES_START]: (state) => ({
    ...state,
    sendingAdminInvites: true
  }),

  [SEND_ADMIN_INVITES_SUCCESS]: (state) => ({
    ...state,
    sendingAdminInvites: false
  }),

  [SEND_ADMIN_INVITES_ERROR]: (state) => ({
    ...state,
    sendingAdminInvites: false
  }),

  [VALIDATE_ADMIN_EMAIL]: reduceValidateAdminEmail,
  [VALIDATE_ADMIN_NAME]: reduceValidateAdminName,
  [PROCESS_ADMIN_VALIDATION_ERRORS]: addAdminValidationErrors,
  [BULK_VALIDATE_AGENT_EMAIL]: handleBulkValidateAgentEmail,
  [CLEAR_EMPTY_AGENT_INVITES]: (state) => ({
    ...state,
    agentInvites: _.filter(state.agentInvites, agentInvite => agentInvite.email || agentInvite.name)
  }),
  [CLEAR_SELECTED_SEARCH_RESULTS]: (state) => ({
    ...state,
    selectedSearchResults: [],
    agentInvites: _.filter(state.agentInvites, agentInvite => !agentInvite.suggested)
  }),
  [TOGGLE_IS_AGENT_INVITE_SELECTED]: handleToggleIsAgentInviteSelected,
  [UNSELECT_ALL_AGENT_INVITES]: (state) => ({
    ...state,
    agentInvites: _.map(state.agentInvites, agentInvite => ({...agentInvite, selected: false}))
  }),
  [LOAD_SELECTED_SEARCH_RESULTS]: (state, { payload }) => ({
    ...state,
    selectedSearchResults: payload.selectedSearchResults
  }),
  [EDIT_SELECTED_AGENT_INVITES]: (state, { payload }) => ({
    ...state,
    agentInvites: _.map(state.agentInvites, invite => invite.selected ? ({...invite, ...payload}) : invite)
  }),
}, cleanState())
